import { z } from "zod";

import { isLinkUrl, withDefaultScheme } from "@/lib/urls";

// The browser seam for the community reads: what the Community ID field
// (#708) and the communities page (#744, below) may ask the BFF to fetch. The
// wire faces for `/internal/communities/*` are the other seam and live on
// their `endpoint()` declarations (`@/server/data/internal-api/communities`) —
// see `client/docs/architecture.md` L2, "Two seams, two homes for a schema".
//
// The search's term passes through as the field processed it (`processSearchTerm`,
// `@/lib/community-id-search`). Trimming and the empty-term rule are Python's
// (`search_communities`), where they bind every caller. No length bound, as in
// the source: a term longer than any id or name matches nothing, and a bound
// here would turn that empty list into a scrubbed validator failure.

export const communitySearchInputSchema = z.strictObject({
  search: z.string().optional(),
});

export type CommunitySearchBrowserInput = z.infer<typeof communitySearchInputSchema>;

// ---------------------------------------------------------------------------
// The communities list (#744)
// ---------------------------------------------------------------------------
//
// What the communities page may ask the BFF to fetch, the vocabularies its
// controls offer, and the page's URL — the donor island's
// (`timmy/src/domains/communities/islands/CommunityListIsland/
// CommunityListIsland.tsx`, cited as CLI). The vocabularies are pinned to
// Python's use case (`server/app/use_cases/communities/list_communities.py`) by
// `community-list-rules-drift.unit.test.ts`.
//
// **The term stays raw.** The URL carries the debounced term as typed, and the
// request sends it as typed: Python processes it once (`processSearchTerm` is
// not idempotent), so nothing here calls it.

/** The Show menu's values, in its order (CLI:74-81). */
export const COMMUNITY_LIST_STATUSES = [
  "all",
  "active",
  "closed",
  "coming_soon",
  "sold_out",
  "unknown",
] as const;

export type CommunityListStatus = (typeof COMMUNITY_LIST_STATUSES)[number];

/** The label each Show option shows (CLI:74-81). */
export const COMMUNITY_LIST_STATUS_LABELS: Record<CommunityListStatus, string> = {
  all: "All",
  active: "Active",
  closed: "Closed",
  coming_soon: "Coming soon",
  sold_out: "Sold out",
  unknown: "Unknown",
};

/** The Show menu's default, and where an unknown URL value falls (CLI:112-113). */
export const COMMUNITY_LIST_DEFAULT_STATUS: CommunityListStatus = "active";

/** The page-size menu and its default (CLI:114,543). */
export const COMMUNITY_LIST_PAGE_SIZES = [5, 10, 20] as const;
export type CommunityListPageSize = (typeof COMMUNITY_LIST_PAGE_SIZES)[number];
export const COMMUNITY_LIST_DEFAULT_PAGE_SIZE: CommunityListPageSize = 20;

/** The six sortable columns (`communities-data-table.tsx`): ID, Name, Status,
 *  City, State, Division. Homesites is not sortable — a feed value. */
export const COMMUNITY_LIST_SORT_FIELDS = [
  "communityId",
  "name",
  "status",
  "city",
  "state",
  "divisionName",
] as const;
export type CommunityListSortField = (typeof COMMUNITY_LIST_SORT_FIELDS)[number];

const communityListSortInput = z.strictObject({
  field: z.enum(COMMUNITY_LIST_SORT_FIELDS),
  direction: z.enum(["asc", "desc"]),
});

export type CommunityListSort = z.infer<typeof communityListSortInput>;

/** The list read's input, browser seam: the same fields as the wire's list
 *  query, the window bound to the menu. */
export const communityListInputSchema = z.strictObject({
  status: z.enum(COMMUNITY_LIST_STATUSES),
  page: z.number().int().min(1),
  pageSize: z.union(COMMUNITY_LIST_PAGE_SIZES.map((size) => z.literal(size))),
  search: z.string().optional(),
  showHidden: z.boolean(),
  missingSpecs: z.boolean(),
  divisionId: z.number().int().nullable(),
  sort: communityListSortInput.nullable(),
});

export type CommunityListInput = z.infer<typeof communityListInputSchema>;

// --- the URL ----------------------------------------------------------------
//
// Everything here is total: the URL is user-editable, so a malformed value is
// an ordinary case read as best it can, never an error. The sort, the map's
// visibility, the selected community and the division are never in the URL —
// the sort and the selection are the page's state, the division the shared
// selector's.

/** The page's URL state, every param read. */
export interface CommunityListUrlState {
  /** The debounced term, raw — untrimmed, unprocessed (CLI:271). */
  search: string;
  status: CommunityListStatus;
  showHidden: boolean;
  missingSpecs: boolean;
  page: number;
  pageSize: CommunityListPageSize;
}

export const COMMUNITY_LIST_URL_DEFAULTS: CommunityListUrlState = {
  search: "",
  status: COMMUNITY_LIST_DEFAULT_STATUS,
  showHidden: false,
  missingSpecs: false,
  page: 1,
  pageSize: COMMUNITY_LIST_DEFAULT_PAGE_SIZE,
};

/** The URL's params as the router holds them (`parseAppSearch`): `search` as
 *  literal text, the rest JSON-read, so `?page=3` is a number and
 *  `?showHidden=true` a boolean. */
export type CommunityListSearchParams = Partial<Record<keyof CommunityListUrlState, unknown>>;

const isStatus = (value: unknown): value is CommunityListStatus =>
  (COMMUNITY_LIST_STATUSES as readonly unknown[]).includes(value);

const isPageSize = (value: unknown): value is CommunityListPageSize =>
  (COMMUNITY_LIST_PAGE_SIZES as readonly unknown[]).includes(value);

/** A positive whole number, as a number or a digit string; else `null`. */
function positiveWhole(value: unknown): number | null {
  const number =
    typeof value === "number"
      ? value
      : typeof value === "string" && /^\d+$/.test(value)
        ? Number(value)
        : NaN;
  return Number.isSafeInteger(number) && number >= 1 ? number : null;
}

/** The donor's `=== 'true'` (CLI:131,182), over the JSON-read boolean too. */
const isTrue = (value: unknown) => value === true || value === "true";

/**
 * The page's URL read (CLI:112-135). `status` outside the six → `active`;
 * `showHidden` / `missingSpecs` true only for `true`. Two named breaks (#723):
 * **`page` is honoured** — anything not a positive whole number reads as 1 —
 * where the donor's mount-time reset sent every link to page 1; and
 * **`pageSize` off the menu → 20**, where the donor took any positive number.
 */
export function parseCommunityListSearch(params: CommunityListSearchParams): CommunityListUrlState {
  const pageSize = positiveWhole(params.pageSize);
  return {
    search: typeof params.search === "string" ? params.search : "",
    status: isStatus(params.status) ? params.status : COMMUNITY_LIST_DEFAULT_STATUS,
    showHidden: isTrue(params.showHidden),
    missingSpecs: isTrue(params.missingSpecs),
    page: positiveWhole(params.page) ?? 1,
    pageSize: isPageSize(pageSize) ? pageSize : COMMUNITY_LIST_DEFAULT_PAGE_SIZE,
  };
}

/**
 * The page's URL write (CLI:270-279): every param at its default omitted —
 * `search` empty, `status` `active` (so `all` *is* written), the toggles off,
 * `page` 1, `pageSize` 20. Nothing else is kept: the donor's rewrite drops any
 * param it does not own.
 */
export function serializeCommunityListSearch(
  state: CommunityListUrlState,
): Partial<CommunityListUrlState> {
  return {
    ...(state.search === "" ? {} : { search: state.search }),
    ...(state.status === COMMUNITY_LIST_DEFAULT_STATUS ? {} : { status: state.status }),
    ...(state.showHidden ? { showHidden: true } : {}),
    ...(state.missingSpecs ? { missingSpecs: true } : {}),
    ...(state.page > 1 ? { page: state.page } : {}),
    ...(state.pageSize === COMMUNITY_LIST_DEFAULT_PAGE_SIZE ? {} : { pageSize: state.pageSize }),
  };
}

/** The filters a user changes; the window is the pager's. */
export type CommunityListFilterPatch = Partial<
  Pick<CommunityListUrlState, "search" | "status" | "showHidden" | "missingSpecs">
>;

/**
 * The navigation a filter **or division** change makes (CLI:279,283-285): the
 * patch over the current URL, **page reset to 1**, defaults omitted, in
 * **replace** mode so Back never replays a keystroke. A division change passes
 * no patch — the division lives in the selector, but the page still resets. A
 * sort change makes no navigation at all: the sort is not in the URL. The
 * donor resets on the **raw** debounced term, so the page makes this
 * navigation on every raw change — a trailing space included — even where the
 * processed term, and so the query key, is unchanged.
 */
export function communityListFilterNavigation(patch: CommunityListFilterPatch = {}) {
  return replaceNavigation({ ...patch, page: 1 });
}

/** The navigation the pager makes: page and size as given, filters kept,
 *  replace mode (CLI:279). */
export function communityListPageNavigation(window: {
  page: number;
  pageSize: CommunityListPageSize;
}) {
  return replaceNavigation(window);
}

/** Every URL write the page makes: `next` over the URL as read, written back
 *  with defaults omitted, replacing the history entry (CLI:279). */
function replaceNavigation(next: Partial<CommunityListUrlState>) {
  return {
    search: (prev: CommunityListSearchParams) =>
      serializeCommunityListSearch({ ...parseCommunityListSearch(prev), ...next }),
    replace: true,
  } as const;
}

/**
 * The read's input for what the page shows: the URL state, the selected
 * division (`null` is "View All") and the current sort (`null` is the source
 * default, by id). The term goes raw, and an empty one is not sent — so an
 * empty box and an absent param are one cache key.
 */
export function communityListQuery(
  state: CommunityListUrlState,
  { divisionId, sort }: { divisionId: number | null; sort: CommunityListSort | null },
): CommunityListInput {
  return {
    status: state.status,
    page: state.page,
    pageSize: state.pageSize,
    ...(state.search === "" ? {} : { search: state.search }),
    showHidden: state.showHidden,
    missingSpecs: state.missingSpecs,
    divisionId,
    sort,
  };
}

// ---------------------------------------------------------------------------
// One community: details and map data (#745)
// ---------------------------------------------------------------------------
//
// What the community page (#751) and every Homesite map mount (#748, #749) may
// ask the BFF to fetch, and the page's route-id read — the donor island's
// (`timmy/src/domains/communities/islands/CommunityDetailsIsland/
// CommunityDetailsIsland.tsx`, cited as CDI).

/** The two one-community reads' input: a positive whole id. */
export const communityDetailsInputSchema = z.strictObject({
  communityId: z.number().int().min(1),
});

export type CommunityDetailsInput = z.infer<typeof communityDetailsInputSchema>;

// --- hide/unhide (#752) ------------------------------------------------------

/**
 * Hide or unhide one community from the list's ⋮ menu: the **target** state
 * (the source's `isHidden: !currentlyHidden`) and the page's own list input,
 * verbatim, so the corrected page lands in the key on screen (ADR-0014
 * decision 4). The id is positive, Falcon's `communityId > 0`.
 */
export const communitySetHiddenInputSchema = z.strictObject({
  communityId: z.number().int().min(1),
  hidden: z.boolean(),
  listQuery: communityListInputSchema,
});

export type CommunitySetHiddenInput = z.infer<typeof communitySetHiddenInputSchema>;

// --- the community link verbs (#754) ------------------------------------------

/** The donor dialog's messages (`LinkDialog.tsx`), as react-hook-form showed them. */
export const COMMUNITY_LINK_REQUIRED_MESSAGE = "This field is required";
export const COMMUNITY_LINK_INVALID_URL_MESSAGE = "Invalid URL";

/**
 * The Add / Edit Link dialog's form, shared with the three server functions'
 * validators (`client/docs/architecture.md` L2, "Two seams, two homes"):
 *
 * - **title** required, as typed — react-hook-form's `required` treats only
 *   `''` as empty, so a space is a title;
 * - **url** required, then the community link rule (`isLinkUrl`, Python's
 *   `is_link_url` by shared vectors); its **output** is the defaulted URL
 *   (`withDefaultScheme`, #724 Q1), so the form keeps what was typed while the
 *   request carries `https://`. Python defaults again, idempotently;
 * - **description** any string — rich text, `''` when the editor is empty.
 *
 * No max lengths here, as the donor's form has none: TIM's (a title over 255,
 * a description over 500) refuse at the server, in Python's field rules, and
 * the dialog shows that as its generic error, as the source does.
 */
export const communityLinkForm = z.strictObject({
  title: z.string().min(1, COMMUNITY_LINK_REQUIRED_MESSAGE),
  url: z
    .string()
    .superRefine((value, ctx) => {
      if (value === "") {
        ctx.addIssue({ code: "custom", message: COMMUNITY_LINK_REQUIRED_MESSAGE });
      } else if (!isLinkUrl(value)) {
        ctx.addIssue({ code: "custom", message: COMMUNITY_LINK_INVALID_URL_MESSAGE });
      }
    })
    .transform(withDefaultScheme),
  description: z.string(),
});

/** The dialog's values, as typed. */
export type CommunityLinkFormValues = z.input<typeof communityLinkForm>;

const positiveId = z.number().int().min(1);

/** Add: the community (Falcon's `communityId > 0`) and the three fields. */
export const communityLinkCreateInputSchema = communityLinkForm.extend({
  communityId: positiveId,
});

/** Edit: the community, the link and the three fields — a full replace. */
export const communityLinkUpdateInputSchema = communityLinkCreateInputSchema.extend({
  linkId: positiveId,
});

/** Delete: the community and the link. */
export const communityLinkDeleteInputSchema = z.strictObject({
  communityId: positiveId,
  linkId: positiveId,
});

/** What a caller sends, as typed (the hooks' variables). */
export type CommunityLinkCreateRequest = z.input<typeof communityLinkCreateInputSchema>;
export type CommunityLinkUpdateRequest = z.input<typeof communityLinkUpdateInputSchema>;
/** What a handler receives, the URL defaulted by the validator. */
export type CommunityLinkCreateInput = z.output<typeof communityLinkCreateInputSchema>;
export type CommunityLinkUpdateInput = z.output<typeof communityLinkUpdateInputSchema>;
export type CommunityLinkDeleteInput = z.output<typeof communityLinkDeleteInputSchema>;

/**
 * What the page makes of its `:internalCommunityId` segment. The donor reads it
 * with a bare `Number(param)` (CDI:45-46) and asks Falcon whatever comes out:
 *
 * - a **positive safe integer** is the id — leading zeros, padding and the other
 *   spellings `Number()` accepts included;
 * - **`"not-found"`** for a segment `Number()` reads as `NaN` — the 404 state
 *   with no wire call, the #724 Q13 ruling — and for a whole number past the
 *   safe range, which the donor sent in digits and TIM did not find;
 * - **any other number** as itself — `0`, a negative, a fraction, `Infinity`,
 *   a number from `1e21` up: the donor asked Falcon for those, and its `gt=0`
 *   or not-an-integer refusal drew the **generic** error state after the
 *   skeletons. The page asks for them too, and `communityDetailsInputSchema`
 *   refuses them — the same skeletons, then the same error, and "Refresh Page"
 *   asks again, as the donor's did.
 *
 * Total: every string, and the absent segment, has an answer.
 */
/** Where `String(number)` switches to exponent form. */
const EXPONENT_FORM_FROM = 1e21;

export function communityIdFromRouteParam(param: string | undefined): number | "not-found" {
  const value = Number(param);
  if (Number.isNaN(value)) return "not-found";
  if (Number.isSafeInteger(value) && value >= 1) return value;
  if (Number.isInteger(value) && value > 0 && value < EXPONENT_FORM_FROM) return "not-found";
  return value;
}
